import React from 'react';
import { Info } from '@material-ui/icons';
import CommonTooltip from 'views/admin/Common/CommonTooltip';
import * as XLSX from 'xlsx';
import { formStudentName, makeFirstCaps } from 'config/constants';
import EmployeeSampleFile from 'assets/sample-files/sample_employee_list.xlsx';
import StudentSampleFile from 'assets/sample-files/sample_student_list.xlsx';
import TimetableSampleFile from 'assets/sample-files/sample_timetable_list.xlsx';
import MarksSampleFile from 'assets/sample-files/sample_marksheet_list.xlsx';
import { store } from '../index';
import { logout, showToast } from 'actions/admin/auth';
// import * as XLSX from 'xlsx';

export const getCurrentYearBatchUtils = () => {
  const currentYr = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const startYear = currentMonth > 5 ? currentYr : currentYr - 1;
  return {
    startYear,
    endYear: startYear + 1,
  };
};

// The next 5 academic-year ranges available to clone into, starting right after the
// latest existing batch's end year (or the current year if there are no batches yet).
// Shared by AcademicYearSetup and CloneBatch - both offer the same "clone to" picker.
export const makeCloneYearOptions = ({ batchList, cloneBatchList }) => {
  const { startYear } = getCurrentYearBatchUtils();
  const maxYearFromBatch = batchList?.length ? Math.max(...batchList.map(el => el.endYear)) : startYear + 1;
  return new Array(5)
    .fill(maxYearFromBatch)
    .map((item, i) => ({
      label: `${item + i} - ${item + i + 1}`,
      value: item + i,
      startYear: item + i,
      endYear: item + i + 1,
    }))
    .filter(el => !cloneBatchList?.map(cto => cto?.startYear).includes(el?.startYear));
};

export const setLocalStore = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getLocalStore = key => {
  if (key && localStorage.getItem(key) && localStorage.getItem(key) !== 'undefined') {
    return JSON.parse(localStorage?.getItem(key));
  }
};

export const setLoggedUserUtils = user => {
  localStorage.setItem('loggedUser', JSON.stringify(user));
};

export const setGlobalSchoolUtils = school => {
  if (school) {
    localStorage.setItem('globalSchool', JSON.stringify(school));
  }
};

export const setGlobalActualCurrentBatchUtils = batch => {
  if (batch?.batchId) localStorage.setItem('actualCurrentBatch', JSON.stringify(batch));
};

export const setGlobalCurrentBatchUtils = batch => {
  if (batch?.batchId) localStorage.setItem('currentBatch', JSON.stringify(batch));
};

export const getLoggedUserUtils = () => {
  if (localStorage.getItem('loggedUser')) {
    return JSON.parse(localStorage.getItem('loggedUser'));
  }
  return null;
};

export const getGlobalSchoolUtils = () => {
  if (localStorage.getItem('globalSchool')) {
    return JSON.parse(localStorage.getItem('globalSchool'));
  }
  return {
    schoolId: null,
    schoolName: null,
    schoolShortName: null,
    address: '',
    phoneNumber: null,
    mobileNumber: null,
    country: '',
    currencyType: '',
    language: '',
    schoolCode: '',
  };
};

export const getGlobalCurrentBatchUtils = (param = 'currentBatch') => {
  const batchData = localStorage.getItem(param);
  if (batchData) {
    return JSON.parse(batchData);
  } else {
    return { batchId: null, startYear: 0, endYear: 0 };
  }
};

export const isActualCurrentBatchOrHigher = () => {
  const currBt = getGlobalCurrentBatchUtils();
  const actCurrBt = getGlobalCurrentBatchUtils('actualCurrentBatch');
  return currBt?.batchId >= actCurrBt?.batchId;
};

export const removeLocalStoreItem = key => {
  if (key) {
    localStorage.removeItem(key);
  }
};

export const handleKeyNumberPress = event => {
  const regex = new RegExp('^[0-9+]*$');
  const key = String.fromCharCode(event.charCode ? event.which : event.charCode);
  if (regex.test(key)) {
    return true;
  }
  return false;
};

export const handleKeyCharacterPress = event => {
  const regex = new RegExp('^[a-zA-Z0-9.]+$');
  const key = String.fromCharCode(event.charCode ? event.which : event.charCode);
  if (regex.test(key)) {
    return true;
  }
  return false;
};

export const makeSubstring = (data, length) => {
  return data?.length > length ? `${data.substring(0, length)}...` : data;
};

export const screenHeight = window.innerHeight;
export const screenWidth = window.innerWidth;
export const isWeb = !!(screenWidth > 820);

export const infoIconSm = () => <Info style={{ fontSize: 14, color: '#6b778c' }} />;

export const getInfoIcon = content => {
  return {
    icon: <CommonTooltip content={content}>{infoIconSm()}</CommonTooltip>,
    iconStyle: { position: 'absolute', marginLeft: '-9px', marginTop: '7px' },
  };
};

export const getSelectAllItem = () => {
  return {
    label: <span style={{ color: 'green', fontWeight: 'bold', fontSize: 15 }}>Select all</span>,
    value: 'select_all',
  };
};

const extractSubCodes = sheetJsonList => {
  const li = sheetJsonList?.map(el => {
    const [, ...rest] = el;
    return rest;
  });
  return li;
};

export const convertXlsxToJson = (file, setExcelJson) => {
  const reader = new FileReader();

  reader.onload = event => {
    try {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
      });

      const list = rows.slice(1);
      const res = extractSubCodes(list);

      setExcelJson(res);
    } catch (err) {
      console.error(err);
      setExcelJson([]);
    }
  };

  reader.onerror = err => {
    console.error(err);
    setExcelJson([]);
  };

  reader.readAsArrayBuffer(file);
};

export const validateExcelJsonList = (li, message) => {
  const isInvalid = li.some(el => {
    return el.some(el1 => !el1);
  });
  if (isInvalid) {
    return (
      message ||
      'Either few subject columns are empty or Some additional empty columns are added, Please upload valid file'
    );
  } else {
    return '';
  }
};

export const validateSubCodesExist = (li, subjectCodes) => {
  const subCodeList = subjectCodes.map(el => el?.code);
  let subCode = '';
  const codeNotExist = li.some(el => {
    return el.some(el1 => {
      if (!subCodeList?.includes(el1)) {
        subCode = el1;
        return true;
      }
    });
  });
  if (codeNotExist) {
    return `${!subCode ? `Few ` : ''} Subject code${
      subCode ? ` [ ${subCode} ]` : ''
    } is not match with configured subjects. Please upload valid file`;
  } else {
    return '';
  }
};

export const convertModuleToName = module => {
  const mName = module?.replace(/_MODULE$/, '');
  const undReplace = mName?.replace(/_/g, ' ');
  return undReplace
    ?.split(' ')
    ?.map(el => makeFirstCaps(el))
    ?.join(' ');
};

export const downloadSampleFile = (fileType, name) => {
  const style = {
    color: '#007bff',
    fontSize: 12,
    fontWeight: 'bold',
  };
  const sampleFiles = {
    employees: (
      <a href={EmployeeSampleFile} download={`${name || 'sample_employee_list'}.xlsx`} style={style}>
        Download Sample File
      </a>
    ),
    students: (
      <a href={StudentSampleFile} download={`${name || 'sample_student_list'}.xlsx`} style={style}>
        Download Sample File
      </a>
    ),
    timetable: (
      <a href={TimetableSampleFile} download={`${name || 'sample_timetable_list'}.xlsx`} style={style}>
        Download Sample File
      </a>
    ),
    marks: (
      <a href={MarksSampleFile} download={`${name || 'sample_marksheet_list'}.xlsx`} style={style}>
        Download Sample File
      </a>
    ),
  };
  return sampleFiles?.[fileType];
};

export function formatMoney(number) {
  return (number || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'INR',
  });
}

export const formResMessage = errObj => {
  const details = errObj?.response?.data?.details || errObj.response?.message || errObj?.response?.data?.message;
  if (!details) {
    return 'Something went wrong, Please try again later';
  } else if (Array.isArray(details) && details?.length) {
    return details.join(', ');
  } else if (typeof details === 'string') {
    return details;
  }
  return 'Something went wrong, Please try again later';
};

export const makeSubText = (text, options) => {
  const textSpan = (
    <span
      style={{
        color: options?.color || '#bcbcbc',
        fontWeight: !options?.dontMakeBold ? 'bold' : 'normal',
        ...(options?.fontSize && { fontSize: options?.fontSize }),
      }}>
      {!options?.noBracket ? `(${text})` : text}
    </span>
  );
  return text ? textSpan : '';
};

let timer;
const handleLogout = () => {
  clearTimeout(timer);
  store.dispatch(logout());
};

export const logoutHandler = error => {
  if (error?.response?.status === 401 && error.response?.data?.error === 'Session expired') {
    store.dispatch(showToast('Session Expired. We are logging you out, Please login again', 'error'));
    timer = setTimeout(() => {
      handleLogout();
    }, 5000);
    return Promise.reject(error);
  } else {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  }
};

export const customFilterOption = (option = {}, searchText) => {
  const { data } = option;
  const isHaveBracket = typeof data?.label === 'string' && data?.label.includes(' (');
  const isHaveHyphen = !isHaveBracket && typeof data?.label === 'string' && data?.label.includes(' - ');

  const labelParts =
    isHaveBracket && typeof data?.label === 'string'
      ? data?.label.split(' (')
      : isHaveHyphen && typeof data?.label === 'string'
      ? data?.label.split(' - ')
      : [data?.label]; // split label into parts

  const value = typeof labelParts?.[0] === 'string' ? labelParts?.[0]?.toLowerCase() : '';
  const suffix = typeof labelParts?.[1] === 'string' ? labelParts?.[1]?.toLowerCase() : '';

  return value?.includes(searchText?.toLowerCase()) || suffix?.includes(searchText?.toLowerCase());
};

export const customGetOptionLabel = option => {
  const isHaveBracket = typeof option?.label === 'string' && option?.label?.includes(' (');
  const isHaveHyphen = !isHaveBracket && typeof option?.label === 'string' && option?.label?.includes(' - ');

  const labelParts = isHaveBracket
    ? option.label.split(' (')
    : isHaveHyphen
    ? option.label.split(' - ')
    : [option?.label]; // split label into parts
  const value = labelParts?.[0]; // first part is the value
  const suffix = labelParts?.[1]; // second part is the suffix

  let suffixStyle = {}; // define empty style object

  // check if suffix contains the substring you want to style
  if (suffix) {
    suffixStyle = {
      color: '#bcbcbc',
      fontWeight: 'bold',
    }; // apply desired style if it does
  }

  // return JSX element with styled suffix
  return (
    <>
      <span>{value}</span>
      {suffix && isHaveBracket && <span style={suffixStyle}> ({suffix}</span>}
      {suffix && isHaveHyphen && <span style={suffixStyle}> - {suffix}</span>}
    </>
  );
};

export const formCourseSelectList = courses => {
  return courses?.map(item => ({
    ...item,
    courseId: item.courseId,
    label: `${item.name} (${item.code})`,
    value: item.courseId,
  }));
};

export const formEmpSelectList = employeeList => {
  return employeeList?.map(item => {
    return {
      ...item,
      employeeId: item.employeeId,
      label: `${item.name} (${item.code})`,
      value: item.employeeId,
      name: item?.name,
    };
  });
};

export const formStudentSelectList = studentList => {
  return studentList.map(el => ({
    ...el,
    label: `${formStudentName(el)} (${el?.registerNumber})`,
    value: el?.studentId,
    name: formStudentName(el),
  }));
};

// Linear search: time complexity of O(n)
// const nextHighestIdForId = list
//   .filter(obj => obj.id > 5)
//   .reduce((acc, obj) => {
//     return obj.id < acc ? obj.id : acc;
//   }, Infinity);

// Binary search to find the next highest id for id: time complexity of O(log n)
export function findNextHighestNumber(arr, targetParamName, targetParamValue) {
  let left = 0;
  let right = arr.length - 1;
  let mid;

  while (left <= right) {
    mid = Math.floor((left + right) / 2);

    if (arr?.[mid]?.[targetParamName] === targetParamValue) {
      // If the target parameter value is found in the array, return the next object's parameter value
      return arr?.[mid + 1] ? arr?.[mid + 1]?.[targetParamName] : null;
    } else if (arr?.[mid]?.[targetParamName] < targetParamValue) {
      // If the middle object's parameter value is less than the target, search the right half of the array
      left = mid + 1;
    } else {
      // If the middle object's parameter value is greater than the target, search the left half of the array
      right = mid - 1;
    }
  }

  // If the target parameter value is not found in the array, return the first object's parameter value
  return arr?.[0]?.[targetParamName];
}

export function findPreviousSmallerNumber(arr, targetParamName, targetParamValue) {
  let left = 0;
  let right = arr.length - 1;
  let mid;

  while (left <= right) {
    mid = Math.floor((left + right) / 2);

    if (arr?.[mid]?.[targetParamName] === targetParamValue) {
      // If the target parameter value is found in the array, return the previous object's parameter value
      return arr?.[mid - 1] ? arr?.[mid - 1]?.[targetParamName] : null;
    } else if (arr?.[mid]?.[targetParamName] < targetParamValue) {
      // If the middle object's parameter value is less than the target, search the right half of the array
      left = mid + 1;
    } else {
      // If the middle object's parameter value is greater than the target, search the left half of the array
      right = mid - 1;
    }
  }

  // If the target parameter value is not found in the array, return the last object's parameter value
  return arr?.[arr.length - 1]?.[targetParamName];
}

const getEmployeeSubjectOptionForm = item => {
  return {
    subjectId: item.subjectId,
    label: `${item.name} (${item.course.name})`,
    value: item.subjectId,
  };
};

export function getGroupedOptions(list, passModule) {
  const moduleWiseGroupName = {
    employeeSubjects: { group: 'course.name', optionFormDb: item => getEmployeeSubjectOptionForm(item) },
  };

  const propertyNames = moduleWiseGroupName?.[passModule]?.group?.split('.');

  const groupedOptions = [];

  list.forEach(item => {
    const courseName = item?.[propertyNames[0]]?.[propertyNames[1]];
    const option = moduleWiseGroupName?.[passModule]?.optionFormDb?.(item);

    // Check if a group already exists for this course name
    const groupIndex = groupedOptions?.findIndex(group => group.label === courseName);

    // If the group doesn't exist, create it
    if (groupIndex === -1) {
      groupedOptions.push({
        label: courseName,
        options: [option],
      });
    } else {
      // If the group exists, add the option to its options array
      groupedOptions[groupIndex].options.push(option);
    }
  });

  return groupedOptions;
}

export function deepEqual(object1, object2) {
  if (object1 === object2) {
    return true;
  }

  if (typeof object1 != 'object' || typeof object2 != 'object' || object1 == null || object2 == null) {
    return false;
  }

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(object1[key], object2[key])) {
      return false;
    }
  }

  return true;
}

export function arraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
}
